/* 
██╗██╗░░░██╗  ██████╗░██╗░██████╗░██╗████████╗░█████╗░██╗░░░░░
██║██║░░░██║  ██╔══██╗██║██╔════╝░██║╚══██╔══╝██╔══██╗██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░██╗░██║░░░██║░░░███████║██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░╚██╗██║░░░██║░░░██╔══██║██║░░░░░
██║╚██████╔╝  ██████╔╝██║╚██████╔╝██║░░░██║░░░██║░░██║███████╗
╚═╝░╚═════╝░  ╚═════╝░╚═╝░╚═════╝░╚═╝░░░╚═╝░░░╚═╝░░╚═╝╚══════╝
╔════════════════════════════════════════════════════════════╗
║              EQUIPO DE DESARROLLO DE SOFTWARE              ║
║                   MODULO DESARROLLADO POR                  ║
╟┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄╢
║ Victor Manuel Calle Londoño                                ║
║ victor.calle@est.iudigital.edu.co                          ║
╚════════════════════════════════════════════════════════════╝
*/

/**
 * Servicio de gestión de fuentes de datos.
 *
 * Este módulo proporciona funciones para interactuar con el backend en operaciones
 * relacionadas con fuentes de datos. Incluye funcionalidades para crear, obtener,
 * actualizar y gestionar fuentes de datos de diferentes tipos (CSV, JSON, EXCEL,
 * bases de datos). Maneja el envío de archivos mediante FormData, incluyendo soporte
 * para imágenes strip opcionales para PKPass de Apple Wallet.
 *
 * Funciones principales:
 * - getDataSources: Obtiene lista paginada de fuentes de datos
 * - getSourceTypes: Obtiene tipos de fuentes disponibles
 * - createSource: Crea nueva fuente con archivos (incluye imagen strip opcional)
 * - createSourceDB: Crea fuente desde conexión a base de datos
 * - updateSource: Actualiza fuente de datos existente (usa POST con FormData para archivos)
 * - getStripImage: Obtiene la imagen strip en formato base64 para preview
 *
 * Nota importante sobre actualización de archivos:
 * - updateSource usa POST cuando se envía FormData (archivos incluidos)
 * - updateSource usa PUT cuando se envía JSON (sin archivos)
 * - Esto es necesario porque Laravel procesa mejor archivos con POST
 *
 * @module dataSourceService
 * @author Victor Manuel Calle Londoño <victor.calle@est.iudigital.edu.co>
 * @date Fecha de creación: 2023-10-18
 * @updated Fecha de última modificación: 2025-10-21
 * @updatedBy Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 */

import fetchWrapper from "fetchWrapper";

//consultar el endpoint que me retornara el listado de tipos de fuentes o error si ocurre y retornar esos datos(no se esta usando)
const getDataSources = async (pageIndex) => {
  try {
    const response = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/data_sources/?page=${pageIndex}`
    );
    // Verificar si el estado de la respuesta es 429 (Too Many Requests)
    if (response.status === 429) {
      // Retornar un mensaje personalizado y un estado de error
      const message =
        "Has excedido el límite de solicitudes. Por favor, intenta de nuevo más tarde.";
      const status = false;
      return { status, message };
    }
    const result = await response.json();
    const { success, code } = result;
    if (success === true) {
      const {
        payload: { data, last_page, total, from, to },
      } = result;
      return { data, last_page, total, from, to };
    } else if (success === false) {
      const message = result.message || "Error del servidor al realizar la solicitud";
      const status = false;
      return { status, message };
      //throw new Error(`Error del servidor al realizar la solicitud`);
    }
  } catch (error) {
    const message = error.message || "Error del servidor al realizar la solicitud";
    const status = false;
    return { status, message };
  }
};

//consultar el endpoint que me retornara el listado de tipos de fuentes o error si ocurre y retornar esos datos
const getSourceTypes = async (pageIndex) => {
  try {
    const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/sources`);
    if (response.status === 429) {
      // Manejar el error de demasiadas peticiones
      // console.error('Error 429: Too Many Requests');
      // Puedes lanzar un error o manejarlo de otra manera según tus necesidades
      // throw new Error('Too Many Requests');
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }
    const result = await response.json();
    const { success, code } = result;
    if (success === true) {
      const { payload } = result;
      return { payload };
    } else if (success === false) {
      const message = result.message || "Error del servidor al realizar la solicitud";
      const status = false;
      return { status, message };
      //throw new Error(`Error del servidor al realizar la solicitud`);
    }
  } catch (error) {
    const message = error.message || "Error del servidor al realizar la solicitud";
    const status = false;
    return { status, message };
  }
};

/**
 * Crea una nueva fuente de datos mediante carga de archivo.
 *
 * Envía los datos de la fuente incluyendo el archivo principal (CSV, JSON o EXCEL)
 * y opcionalmente una imagen strip para PKPass al endpoint del backend.
 * La imagen strip debe ser PNG y no superar 2MB.
 *
 * @param {FormData} data - FormData que contiene todos los campos del formulario,
 *                          incluyendo los archivos (source y strip_image opcional).
 * @returns {Promise<Object>} Objeto con el resultado de la operación:
 *                            - {status: false, message: string} en caso de error
 *                            - {success: true, payload: Object} en caso de éxito
 * @throws {Error} Si ocurre un error durante la petición HTTP.
 */
const createSource = async (data) => {
  try {
    const response = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/sources/upload_file`,
      data,
      "POST"
    );
    if (response.status === 429) {
      // Manejar el error de demasiadas peticiones
      // console.error('Error 429: Too Many Requests');
      // Puedes lanzar un error o manejarlo de otra manera según tus necesidades
      // throw new Error('Too Many Requests');
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }
    const result = await response.json();

    if (result.success === true) {
      return result;
    } else {
      // Extraer mensajes de validación del payload si existen
      let message = "Error del servidor al realizar la solicitud";

      if (result.payload && result.payload.message) {
        // Si payload.message es un objeto con errores de validación
        if (typeof result.payload.message === "object") {
          const errors = Object.values(result.payload.message).flat();
          message = errors.join(". ");
        } else {
          message = result.payload.message;
        }
      } else if (result.message && typeof result.message === "string") {
        message = result.message;
      }

      const status = false;
      return { status, message };
    }
    // return result;
  } catch (error) {
    const message = error.message || "Error del servidor al realizar la solicitud";
    const status = false;
    return { status, message };
  }
};

//consultar el endpoint que me retornara si se creo correctamente la fuente de datos o error si ocurre y retornar esos datos
const createSourceDB = async (data) => {
  try {
    const response = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/sources/upload_db`,
      JSON.stringify(data), // Convierte el objeto 'data' a una cadena JSON y envíalo en el cuerpo de la solicitud.
      "POST" // Usa 'PUT' para actualizar un recurso existente.
    );
    if (response.status === 429) {
      // Manejar el error de demasiadas peticiones
      // console.error('Error 429: Too Many Requests');
      // Puedes lanzar un error o manejarlo de otra manera según tus necesidades
      // throw new Error('Too Many Requests');
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }
    const result = await response.json();
    const { success, code } = result;
    if (success === true) {
      const { payload } = result;
      return result;
    } else if (success === false) {
      const message = result.message || "Error del servidor al realizar la solicitud";
      const status = false;
      return { status, message };
    }
  } catch (error) {
    const message = error.message || "Error del servidor al realizar la solicitud";
    const status = false;
    return { status, message };
  }
};

/**
 * Obtiene la imagen strip de un DataSource en formato base64.
 *
 * Recupera la imagen strip asociada a una fuente de datos específica,
 * que se utiliza para la franja visual en los PKPass de Apple Wallet.
 *
 * @param {string} id - ID del DataSource del cual obtener la imagen strip.
 * @returns {Promise<Object>} Objeto con el resultado de la operación:
 *                            - {has_image: boolean, image: string|null, ...} datos de la imagen
 *                            - {status: false, message: string} en caso de error
 * @throws {Error} Si ocurre un error durante la petición HTTP.
 */
const getStripImage = async (id) => {
  try {
    const response = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/data_sources/data_source/${id}/strip_image`
    );
    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }
    const result = await response.json();
    const { success } = result;
    if (success === true) {
      return result.payload;
    } else if (success === false) {
      const message = result.message || "Error del servidor al realizar la solicitud";
      const status = false;
      return { status, message };
    }
  } catch (error) {
    const message = error.message || "Error del servidor al realizar la solicitud";
    const status = false;
    return { status, message };
  }
};

/**
 * Actualiza una fuente de datos existente, incluyendo imagen strip opcional.
 *
 * Utiliza POST cuando se envía FormData (para archivos) y PUT cuando se envía JSON.
 * Esto es necesario porque Laravel procesa archivos correctamente solo con POST.
 * Cuando se envía una nueva imagen strip, esta reemplaza automáticamente la anterior.
 *
 * Casos de uso:
 * - Actualización de horarios sin imagen: Usa PUT con JSON
 * - Actualización con nueva imagen strip: Usa POST con FormData
 *
 * @param {string} id - ID del DataSource a actualizar (UUID).
 * @param {FormData|Object} data - FormData con archivos (usa POST) o
 *                                 objeto con datos a actualizar (usa PUT).
 *                                 FormData puede incluir:
 *                                 - check_in_time: número de horas
 *                                 - check_in_minute: número de minutos
 *                                 - check_out_time: número de horas
 *                                 - check_out_minute: número de minutos
 *                                 - strip_image: File (PNG, máx 2MB) - opcional
 * @returns {Promise<Object>} Objeto con el resultado de la operación:
 *                            - {status: false, message: string} en caso de error
 *                            - {success: true, payload: Object} en caso de éxito
 * @throws {Error} Si ocurre un error durante la petición HTTP.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@est.iudigital.edu.co>
 * @date Fecha de creación: 2023-10-18
 * @updated Fecha de última modificación: 2025-10-21
 * @updatedBy Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 */
const updateSource = async (id, data) => {
  try {
    // Determinar método HTTP según tipo de datos
    // POST para FormData (archivos) - Laravel requiere POST para procesar archivos correctamente
    // PUT para JSON (sin archivos) - Actualización tradicional REST
    const method = data instanceof FormData ? "POST" : "PUT";

    const requestData = data instanceof FormData ? data : JSON.stringify(data);

    const response = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/data_sources/edit/${id}`,
      requestData,
      method
    );

    if (response.status === 429) {
      const message =
        "Se ha excedido el límite de solicitudes al servidor. Por favor, inténtelo de nuevo más tarde.";
      const status = false;
      return { status, message };
    }
    const result = await response.json();
    const { success, code } = result;
    if (success === true) {
      return result;
    } else if (success === false) {
      const message = result.message || "Error del servidor al realizar la solicitud";
      const status = false;
      return { status, message };
    }
  } catch (error) {
    const message = error.message || "Error del servidor al realizar la solicitud";
    const status = false;
    return { status, message };
  }
};

export {
  getDataSources,
  createSource,
  updateSource,
  getSourceTypes,
  createSourceDB,
  getStripImage,
};
